import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/services/api";
import { OnboardingStep } from "@/utils/onboardingConfig";

/**
 * Hook to update onboarding step
 * 
 * Call this when a user completes a step in the onboarding flow.
 * Automatically refreshes user data to update the cached onboarding step.
 * 
 * @returns updateStep function and updating state
 * 
 * @example
 * ```tsx
 * const { updateStep, updating } = useUpdateOnboardingStep();
 * 
 * await updateStep("signup/3", {
 *   onSuccess: () => navigate("/signup/3"),
 *   onError: (error) => console.error(error)
 * });
 * ```
 */
export const useUpdateOnboardingStep = () => {
  const [updating, setUpdating] = useState(false);
  const { refreshUser } = useAuth();

  const updateStep = async (
    step: OnboardingStep,
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void }
  ) => {
    setUpdating(true);
    try {
      await api.users.updateOnboardingStep(step);
      // Refresh user data to update cached onboardingStep in context
      // This ensures OnboardingGuard uses the latest value without API calls
      await refreshUser();
      options?.onSuccess?.();
    } catch (error) {
      console.error("Error updating onboarding step:", error);
      options?.onError?.(error);
    } finally {
      setUpdating(false);
    }
  };

  return { updateStep, updating };
};
